import React, { useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUserAuthSessionStore, useAppHydrationStore } from '@/store'
import { useStartParamStore } from '@/store/useStartParamStore'

interface AuthSessionWrapperProps {
  children: React.ReactNode
}

const IS_DEVELOPMENT = import.meta.env.DEV

/**
 * Wrapper for auth-related pages (login, signup, etc.)
 *
 * Redirects authenticated users to dashboard.
 * Waits for app hydration before checking auth to prevent
 * flash of login page on reload for authenticated users.
 */
export const AuthSessionWrapper: React.FC<AuthSessionWrapperProps> = ({
  children,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const { token, isSessionOpen } = useUserAuthSessionStore()
  const isHydrated = useAppHydrationStore((state) => state.isHydrated)

  const navigateToDashboard = useCallback(() => {
    const pendingRoute = useStartParamStore.getState().getPendingRoute()
    const destination = pendingRoute || '/dashboard'
    navigate(destination, { replace: true })
    if (pendingRoute) {
      useStartParamStore.getState().markConsumed()
    }
  }, [navigate])

  useEffect(() => {
    // Wait for hydration before checking auth
    if (!isHydrated) return

    // JUST FOR DEVELOPMENT ENVIRONMENT - skip auth checks
    if (IS_DEVELOPMENT && token) {
      // Only redirect if we're on the main page
      if (location.pathname === '/') {
        setTimeout(() => navigateToDashboard(), 100)
      }
      return
    }

    // In production or when session is open, check authentication
    if (isSessionOpen && token) {
      // Only redirect if we're on the main page
      if (location.pathname === '/') {
        setTimeout(() => navigateToDashboard(), 100)
      }
    }
  }, [token, isSessionOpen, location.pathname, navigateToDashboard, isHydrated])

  // Wait for hydration before rendering to prevent flash of signup page
  if (!isHydrated) {
    return null
  }

  return <>{children}</>
}
